import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  Users, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle, 
  HelpCircle,
  Mail,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { useRSVPData, useSubmitRSVP, useTrackEmailEvent } from '../../api/hooks/useEmailInvitations';
import { RSVPStatus } from '@poker-tracker/shared';

const RSVPPage: React.FC = () => {
  const { invitationId } = useParams<{ invitationId: string }>();
  const navigate = useNavigate();
  const [selectedRSVP, setSelectedRSVP] = useState<RSVPStatus>('pending');
  const [playerName, setPlayerName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, isLoading, error } = useRSVPData(invitationId!);
  const submitRSVP = useSubmitRSVP();
  const trackEvent = useTrackEmailEvent();

  // Track that the invitation was opened
  useEffect(() => {
    if (invitationId && data) {
      trackEvent.mutate({
        invitationId,
        eventType: 'opened',
        metadata: {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      });
    }
  }, [invitationId, data]); // Removed trackEvent from dependencies

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitationId || selectedRSVP === 'pending') return;

    setIsSubmitting(true);
    try {
      await submitRSVP.mutateAsync({
        invitationId,
        rsvpStatus: selectedRSVP,
        playerName: playerName.trim() || undefined
      });

      // Track the response
      trackEvent.mutate({
        invitationId,
        eventType: 'responded',
        metadata: {
          rsvpStatus: selectedRSVP,
          timestamp: new Date().toISOString()
        }
      });

      // Success - no automatic redirect
    } catch (error) {
      console.error('Error submitting RSVP:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const errorWithResponse = error as any; // Type assertion for error with response
    const errorMessage = errorWithResponse.response?.status === 404 
      ? 'This invitation was not found or may have been removed.'
      : errorWithResponse.response?.status === 410
      ? 'This invitation has expired.'
      : 'There was an error loading this invitation.';

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invitation Not Available</h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { invitation, game } = data;
  const gameDate = new Date(game.gameDate);
  const isExpired = new Date(invitation.expiresAt) < new Date();
  const hasResponded = invitation.rsvpStatus && invitation.rsvpStatus !== 'pending';

  // If already responded, show confirmation
  if (hasResponded && !submitRSVP.isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            {invitation.rsvpStatus === 'accepted' && (
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            )}
            {invitation.rsvpStatus === 'declined' && (
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            )}
            {invitation.rsvpStatus === 'maybe' && (
              <HelpCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            )}
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            You've Already Responded
          </h1>
          <p className="text-gray-600 mb-2">
            Your RSVP status for <strong>{game.title}</strong> is: 
            <span className={`ml-2 font-semibold ${
              invitation.rsvpStatus === 'accepted' ? 'text-green-600' :
              invitation.rsvpStatus === 'declined' ? 'text-red-600' :
              'text-yellow-600'
            }`}>
              {invitation.rsvpStatus?.charAt(0).toUpperCase() + invitation.rsvpStatus?.slice(1)}
            </span>
          </p>
          <p className="text-sm text-gray-500 mb-6">
            If you need to change your response, please contact the game host directly.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // If submission was successful, show success message
  if (submitRSVP.isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">RSVP Submitted!</h1>
          <p className="text-gray-600 mb-2">
            Thank you for responding to the invitation for <strong>{game.title}</strong>.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            The game host has been notified of your response.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-blue-600 text-white p-6 text-center">
            <Mail className="h-12 w-12 mx-auto mb-4" />
            <h1 className="text-2xl font-bold">You're Invited to Play Poker!</h1>
            <p className="text-blue-100 mt-2">
              {invitation.senderName} has invited you to join their poker game
            </p>
          </div>

          {/* Game Details */}
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{game.title}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Date & Time</p>
                  <p className="font-medium">
                    {gameDate.toLocaleDateString()} at {gameDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <MapPin className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-medium">{game.location}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Game Type</p>
                  <p className="font-medium">{game.gameType}</p>
                  {game.blinds && <p className="text-sm text-gray-500">Blinds: {game.blinds}</p>}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <DollarSign className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Buy-in Range</p>
                  <p className="font-medium">${game.minBuyIn} - ${game.maxBuyIn}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Players</p>
                  <p className="font-medium">Max {game.maxPlayers} players</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Payment Methods</p>
                  <p className="font-medium">{game.paymentMethods.join(', ')}</p>
                  {game.venmoId && <p className="text-sm text-gray-500">Venmo: {game.venmoId}</p>}
                </div>
              </div>
            </div>

            {/* Custom Message */}
            {invitation.customMessage && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-yellow-800 mb-2">Message from {invitation.senderName}:</h3>
                <p className="text-yellow-700">{invitation.customMessage}</p>
              </div>
            )}

            {/* Expiration Warning */}
            {isExpired && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  <h3 className="font-medium text-red-800">This invitation has expired</h3>
                </div>
                <p className="text-red-700 mt-1">
                  This invitation expired on {new Date(invitation.expiresAt).toLocaleDateString()}.
                  Please contact the game host if you still want to join.
                </p>
              </div>
            )}

            {/* RSVP Form */}
            {!isExpired && (
              <form onSubmit={handleSubmit}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Please respond to this invitation:</h3>
                
                {/* Player Name Input */}
                <div className="mb-4">
                  <label htmlFor="playerName" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name {selectedRSVP === 'accepted' && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="text"
                    id="playerName"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={invitation.recipientName || "Enter your name"}
                    required={selectedRSVP === 'accepted'}
                  />
                </div>

                {/* RSVP Options */}
                <div className="space-y-3 mb-6">
                  <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="rsvp"
                      value="accepted"
                      checked={selectedRSVP === 'accepted'}
                      onChange={(e) => setSelectedRSVP(e.target.value as RSVPStatus)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="ml-3 flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <span className="font-medium text-gray-900">Yes, I'll be there!</span>
                    </div>
                  </label>

                  <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="rsvp"
                      value="declined"
                      checked={selectedRSVP === 'declined'}
                      onChange={(e) => setSelectedRSVP(e.target.value as RSVPStatus)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="ml-3 flex items-center space-x-2">
                      <XCircle className="h-5 w-5 text-red-500" />
                      <span className="font-medium text-gray-900">Sorry, I can't make it</span>
                    </div>
                  </label>

                  <label className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="rsvp"
                      value="maybe"
                      checked={selectedRSVP === 'maybe'}
                      onChange={(e) => setSelectedRSVP(e.target.value as RSVPStatus)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="ml-3 flex items-center space-x-2">
                      <HelpCircle className="h-5 w-5 text-yellow-500" />
                      <span className="font-medium text-gray-900">Maybe, I'm not sure yet</span>
                    </div>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={selectedRSVP === 'pending' || isSubmitting}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <span>Submit RSVP</span>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>This invitation was sent via Poker Tracker</p>
          <p className="mt-1">
            Questions? Contact {invitation.senderName} directly.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RSVPPage;
